<script setup lang="ts">
import {computed, ref} from "vue";
import {ChevronLeft, ChevronRight, FileStack, FileText, Search, ShoppingCart} from "@lucide/vue";
import {Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious} from "@/core/components/ui/carousel";
import BaseProductSearchBar from "@/product/components/BaseProductSearchBar.vue";
import MedicationCheckupPrescriptionSheet from "@/medication-checkup/components/MedicationCheckupPrescriptionSheet.vue";
import MedicationCheckupTreatmentTile from "@/medication-checkup/components/MedicationCheckupTreatmentTile.vue";
import {LegacyProduct} from "@/core/interfaces/Product";
import {TreatmentLine} from "@/medication-checkup/interfaces/treatment-selections";
import TreatmentSheets, {TreatmentSheet} from "@/medication-checkup/services/TreatmentSheets";
import {useTreatmentSelectionsStore} from "@/medication-checkup/stores/treatment-selections-store";
import {CHIP} from "@/medication-checkup/services/SceneClasses";
import {pluralize} from "@/core/filters/Pluralize";

/**
 * The collection under review, in one section: the prescriptions as sheets
 * to check the delivered products on, the counter purchases, the products
 * added during the interview, and the search that adds one.
 *
 * Checks and posologies go to the review store; what reaches the server —
 * adding, deleting — goes up to the scene.
 */

const props = defineProps<{
    lines: TreatmentLine[];
    /**
     * What the collection must not leave out, as the handler words it.
     */
    reminder: string | null;
    adding: boolean;
    /**
     * The lines being deleted: they leave the section at once, and come back
     * if the server refuses.
     */
    removing: number[];
}>();

const emit = defineEmits<{
    edit: [line: TreatmentLine];
    open: [sheets: TreatmentSheet[], index: number];
    add: [productId: number, source: TreatmentLine['source']];
    remove: [line: TreatmentLine];
}>();

const selection = useTreatmentSelectionsStore();

const shown = computed(() => props.lines.filter(line => !props.removing.includes(line.id)));

const sources = computed(() => TreatmentSheets.sources(shown.value));

/**
 * The sheets the full screen viewer pages through: those with a document.
 */
const scanned = computed(() => sources.value.sheets.filter(sheet => sheet.prescriptionKey !== null));

const open = (sheet: TreatmentSheet) => emit('open', scanned.value, scanned.value.indexOf(sheet));

const retainedCount = computed(() => shown.value.filter(selection.isChecked).length);

/**
 * The arrows of the strip, half way down the page, gone at the end they
 * cannot pass. The strip is dragged or stepped, never wheeled: the wheel
 * keeps scrolling the thread and the lists of the sheets.
 */
const ARROW = 'top-[339px] z-20 size-11 [&_svg]:size-7 border-solid border-slate-200 text-primary shadow-lg '
    + 'hover:bg-lightPurple-lightest disabled:opacity-0 disabled:pointer-events-none';

/**
 * Where the product about to be added comes from: the scan only ever sees
 * dispensed products, so a hand-added one needs the pharmacist to say
 * whether it was prescribed or self-medicated.
 */
const source = ref<TreatmentLine['source']>('prescription');

/**
 * Remounting the search bar is how it forgets a selection: adding the chosen
 * product empties the row for the next one.
 */
const searchGeneration = ref(0);

const add = (product: LegacyProduct) => {
    emit('add', product.id, source.value);
    searchGeneration.value += 1;
};

const SOURCES = [
    {key: 'prescription', label: 'Sur ordonnance', icon: FileText},
    {key: 'automedication', label: 'Hors ordonnance', icon: ShoppingCart},
] as const;
</script>

<template>
  <div
    class="flex flex-col gap-2 rounded-[14px] border border-solid border-slate-200 bg-white p-3.5 shadow-custom"
    :style="{width: 'min(1040px, 100cqw)', marginLeft: 'calc((100% - min(1040px, 100cqw)) / 2)'}"
  >
    <div class="flex items-center gap-2">
      <FileStack class="size-[19px] text-primary" />
      <span class="flex flex-1 flex-col">
        <span class="text-[14px] font-bold text-primary">Sélectionnez les traitements du patient</span>
        <span class="text-[11.5px] text-muted-foreground">Ordonnances, achats comptoir et ajouts pendant l'entretien</span>
      </span>
      <span class="whitespace-nowrap text-[12px] font-semibold text-secondary-darkest">
        {{ retainedCount }} / {{ shown.length }} retenus
      </span>
    </div>

    <span class="flex items-center gap-1.5 border-t border-solid border-gray-light pt-2">
      <span :class="CHIP" class="gap-1 bg-lightPurple-lightest text-primary">
        <FileText class="size-3" />
        Sur ordonnance
      </span>
      <span class="text-[11px] text-muted-foreground">
        <template v-if="sources.sheets.length > 0">
          {{ sources.sheets.length }} {{ pluralize('ordonnance', sources.sheets.length) }} · cochez les produits à retenir pour le bilan
        </template>
        <template v-else>Aucune ordonnance retrouvée sur 12 mois</template>
      </span>
    </span>

    <Carousel
      v-if="sources.sheets.length > 0"
      :opts="{align: 'start', containScroll: 'trimSnaps'}"
    >
      <CarouselContent class="ml-0 p-[3px] pb-1.5">
        <CarouselItem
          v-for="sheet in sources.sheets"
          :key="sheet.key"
          class="basis-auto pl-0 pr-4"
        >
          <MedicationCheckupPrescriptionSheet
            :sheet="sheet"
            @open="open(sheet)"
            @edit="emit('edit', $event)"
          />
        </CarouselItem>
      </CarouselContent>

      <CarouselPrevious :class="[ARROW, 'left-1']">
        <ChevronLeft class="size-7" />
        <span class="sr-only">Ordonnance précédente</span>
      </CarouselPrevious>
      <CarouselNext :class="[ARROW, 'right-1']">
        <ChevronRight class="size-7" />
        <span class="sr-only">Ordonnance suivante</span>
      </CarouselNext>
    </Carousel>

    <div class="flex flex-col gap-1.5 border-t border-solid border-gray-light pt-2.5">
      <span class="flex items-center gap-1.5">
        <span :class="CHIP" class="gap-1 bg-lightPurple-lightest text-primary">
          <ShoppingCart class="size-3" />
          Hors ordonnance
        </span>
        <span class="text-[11px] text-muted-foreground">Achats comptoir · 12 derniers mois</span>
      </span>
      <span v-if="reminder" class="text-[11px] font-medium text-gray-dark">{{ reminder }}</span>
      <div v-if="sources.counter.length > 0" class="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
        <MedicationCheckupTreatmentTile
          v-for="line in sources.counter"
          :key="line.id"
          :line="line"
          @edit="emit('edit', line)"
        />
      </div>
      <span v-else class="text-[11px] italic text-muted-foreground">Aucun achat au comptoir sur 12 mois</span>
    </div>

    <div class="flex flex-col gap-1.5 border-t border-solid border-gray-light pt-2.5">
      <span class="flex items-center gap-1.5">
        <span :class="CHIP" class="gap-1 bg-gray-light text-gray-dark">
          <Search class="size-3" />
          Autre traitement
        </span>
        <span class="text-[11px] text-muted-foreground">Non retrouvé ? Ajoutez-le par recherche</span>
      </span>
      <div class="flex items-center gap-2">
        <div class="flex shrink-0 gap-0.5 rounded-full bg-gray-light p-0.5">
          <button
            v-for="option in SOURCES"
            :key="option.key"
            type="button"
            class="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold"
            :class="source === option.key ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground'"
            @click="source = option.key"
          >
            <component :is="option.icon" class="size-3.5" />
            {{ option.label }}
          </button>
        </div>
        <BaseProductSearchBar
          :key="searchGeneration"
          class="flex-1"
          :disabled="adding"
          placeholder="Rechercher un produit, une molécule…"
          @select="add"
        />
      </div>

      <div v-if="sources.added.length > 0" class="mt-1 flex flex-col gap-1.5">
        <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Ajoutés pendant l'entretien</span>
        <div class="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
          <MedicationCheckupTreatmentTile
            v-for="line in sources.added"
            :key="line.id"
            :line="line"
            removable
            @edit="emit('edit', line)"
            @remove="emit('remove', line)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
